import { ISearchResponse } from 'apiConnector/blotters/common';
import type { IBlotterItem } from 'models/IBlotterItem';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { toUtcDate, toUtcDateFromDate, toUtcEndOfDay, toUtcStartOfDay } from 'state/blotters/common/blotter.utils';
import type { ITradeRequest } from 'state/blotters/secondaryMarketTradeBlotter/secondaryMarketTradeBlotter.selector';
import { now } from 'utils/core/now';
import type { IHttpClient } from 'utils/http.old/httpClient.model';

export interface SecondaryMarketBlotterFetchCsvApi {
  fetchCSV: (params: ITradeRequest) => Observable<string>;
}

export type SecondaryMarketBlotterApi = SecondaryMarketBlotterFetchCsvApi &
  IFetchColumnDefinitionEpicApi &
  IFetchItemsEpicApi;

interface IFetchColumnDefinitionEpicApi {
  fetchColumnDefinition(): Observable<IBlotterColumnDefinition<IBlotterItem>[]>;
}

interface IFetchItemsEpicApi {
  search(request: ITradeRequest, dateNow: string): Observable<ISearchResponse<IBlotterItem>>;
}

interface IColumnDefinitionResponse {
  columnDefinitions: IBlotterColumnDefinition<IBlotterItem>[];
}

function toParamsData(request: ITradeRequest, dateNow?: string) {
  const params: any = {
    page: request.page,
    size: request.itemsPerPage,
    startDate: dateNow
      ? toUtcDateFromDate(request.dateFilter, toUtcStartOfDay(new Date(dateNow))).toISOString()
      : toUtcDate(request.dateFilter).toISOString(),
    endDate: dateNow ? toUtcEndOfDay(new Date(dateNow)).toISOString() : toUtcEndOfDay(now()).toISOString(),
    activityScope: request.activityFilter,
    sortBy: 'TradeDate',
    sortDir: 'DESC',
  };
  if (request.quickSearchTerm) {
    params.fullTextSearch = request.quickSearchTerm;
  }
  if (request.advancedFilters) {
    if (request.currencyFilter && request.currencyFilter !== 'allCurrencies') {
      params.currency = request.currencyFilter;
    }
  }
  if (request.platformFilter && request.platformFilter !== 'AllPlatforms') {
    params.platform = request.platformFilter;
  }
  return params;
}
export const apiConnector = (http$: IHttpClient): SecondaryMarketBlotterApi => ({
  fetchCSV: (request: ITradeRequest) => {
    return http$
      .getRAW({
        url: 'api/v1/secondary/blotter/csv',
        params: toParamsData(request),
      })
      .pipe(map(({ payload }) => payload));
  },
  fetchColumnDefinition: () => {
    return http$
      .getJSON<IColumnDefinitionResponse>({
        url: 'api/v1/secondary/blotter/columnDefinitions',
      })
      .pipe(map(({ payload }) => payload.columnDefinitions));
  },
  search: (request: ITradeRequest, dateNow: string) => {
    return http$
      .getJSON<ISearchResponse<IBlotterItem>>({
        url: 'api/v1/secondary/blotter/trades',
        params: toParamsData(request, dateNow),
      })
      .pipe(map(({ payload }) => payload));
  },
});
